import {
  type StaffProgrammingData,
  StaffProgrammingExperience,
  type StaffProgrammingWorkout,
} from '@/components/programming/StaffProgrammingExperience';
import {
  WorkoutForm,
  type WorkoutFormData,
  normalizeWorkoutFormData,
} from '@/components/programming/WorkoutForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useApi } from '@/hooks/useApi';
import { useEffectiveRole } from '@/stores/authStore';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';

interface WorkoutsResponse {
  items: StaffProgrammingWorkout[];
  total: number;
}

interface PersonalLibraryResponse {
  workouts: StaffProgrammingWorkout[];
}

/**
 * Shared Programming/Workouts page.
 *
 * Roles with programming access use the unified Calendar/List programming
 * experience, with score logging available from each workout card.
 */
export default function Programming() {
  const { api } = useApi();
  const effectiveRole = useEffectiveRole();
  const queryClient = useQueryClient();
  const [showWorkoutForm, setShowWorkoutForm] = useState(false);
  const [isCreatingWorkout, setIsCreatingWorkout] = useState(false);

  if (effectiveRole === 'affiliate_coach') {
    return <Navigate to="/affiliate/programming" replace />;
  }

  if (effectiveRole === 'hq_admin') {
    return (
      <StaffProgrammingExperience
        title="Programming"
        description="Review Daily WODs, Standards, and Specialty programming by month."
        queryKey={['staff-programming', 'hq-admin']}
        primaryAction={{
          label: 'Manage Programming',
          icon: 'calendar',
          href: '/admin/programming',
        }}
        canLogScores
        fetchMonth={async ({ year, month }): Promise<StaffProgrammingData> => {
          const response = await api.get<WorkoutsResponse>(
            `/programming/workouts?year=${year}&month=${month}&limit=100`
          );
          if (!response.success) throw new Error(response.error);
          return { workouts: response.data?.items || [] };
        }}
      />
    );
  }

  if (
    effectiveRole === 'agent' ||
    effectiveRole === 'affiliate_owner' ||
    effectiveRole === 'member' ||
    effectiveRole === 'individual'
  ) {
    const roleLabel =
      effectiveRole === 'agent'
        ? 'Agent'
        : effectiveRole === 'affiliate_owner'
          ? 'Gym owner'
          : effectiveRole === 'member'
            ? 'Member'
            : 'Individual';
    const queryKey = ['personal-programming', effectiveRole] as const;
    const canCreateWorkout =
      effectiveRole === 'agent' ||
      effectiveRole === 'affiliate_owner' ||
      effectiveRole === 'member' ||
      effectiveRole === 'individual';

    const createPersonalWorkout = async (formData: WorkoutFormData) => {
      setIsCreatingWorkout(true);
      try {
        const response = await api.post('/programming/personal-workouts', {
          ...normalizeWorkoutFormData(formData),
          shared_to_global: formData.shared_to_global ?? false,
          is_favorite: formData.is_favorite ?? false,
        });
        if (!response.success) throw new Error(response.error || 'Failed to create workout');
        await queryClient.invalidateQueries({ queryKey: [...queryKey] });
        setShowWorkoutForm(false);
        toast.success('Workout added to My Workouts');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to create workout');
      } finally {
        setIsCreatingWorkout(false);
      }
    };

    return (
      <>
        <StaffProgrammingExperience
          title="Programming"
          description="Browse Daily WODs and Standards, then plan workouts on your personal calendar."
          queryKey={queryKey}
          showSpecialty={false}
          showFavoriteFilter
          primaryAction={
            canCreateWorkout
              ? { label: 'Create Workout', icon: 'plus', onClick: () => setShowWorkoutForm(true) }
              : undefined
          }
          canLogScores
          showListMonthControls={false}
          onScoreLogged={() => queryClient.invalidateQueries({ queryKey: [...queryKey] })}
          fetchMonth={async (): Promise<StaffProgrammingData> => {
            const response = await api.get<PersonalLibraryResponse>(
              '/programming/personal-library'
            );
            if (!response.success) throw new Error(response.error);
            return { workouts: response.data?.workouts || [] };
          }}
          onScheduleWorkout={async (workout, schedule) => {
            const response = await api.post('/programming/personal-calendar', {
              workout_id: workout.id,
              ...schedule,
            });
            if (!response.success) {
              throw new Error(response.error || `Failed to schedule workout for ${roleLabel}`);
            }
            await queryClient.invalidateQueries({ queryKey: [...queryKey] });
          }}
          onToggleFavorite={async (workout) => {
            const response = await api.post(`/programming/workouts/${workout.id}/favorite`, {
              is_favorite: !workout.is_favorite,
            });
            if (!response.success) {
              throw new Error(response.error || 'Failed to update Favourite');
            }
            await queryClient.invalidateQueries({ queryKey: [...queryKey] });
          }}
        />
        <Dialog open={showWorkoutForm} onOpenChange={setShowWorkoutForm}>
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-0 bg-transparent p-0">
            <DialogTitle className="sr-only">Create Workout</DialogTitle>
            <DialogDescription className="sr-only">
              Create a personal workout for My Workouts.
            </DialogDescription>
            <WorkoutForm
              variant="content"
              title="Create Workout"
              subtitle="Create a personal workout for My Workouts."
              visibilityMode="individual"
              showFavorite
              isSubmitting={isCreatingWorkout}
              onSubmit={createPersonalWorkout}
              onCancel={() => setShowWorkoutForm(false)}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Programming</CardTitle>
          <CardDescription>Workout programming is available to active users.</CardDescription>
        </CardHeader>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">Programming not available for your role.</p>
        </CardContent>
      </Card>
    </div>
  );
}
